import { type WindowLayout } from 'generated/graphql';
import { type PopupOptions } from 'utils/openPopup';
import { isFiniteNumber } from 'utils/math';
import { type WorkspaceDisplayArrangement } from './state';
import { getPlaceableScreenName, resolveRelativeRect } from './utils';

interface GetWorkspaceLayoutPopupOptionsArgs {
  url: string;
  layoutWindows: Nullable<ReadonlyArray<WindowLayout>>;
  displayArrangement: Nullable<WorkspaceDisplayArrangement>;
}

// Where a new window should open so it starts in its spot in the layout, if the layout has one
export function getWorkspaceLayoutPopupOptions({
  url,
  layoutWindows,
  displayArrangement,
}: GetWorkspaceLayoutPopupOptionsArgs): PopupOptions | undefined {
  const screen = getPlaceableScreenName(url);
  if (screen == null) return undefined;

  const windowLayout = layoutWindows?.find((layoutWindow) => layoutWindow.screen === screen);
  if (windowLayout == null) return undefined;

  const displaySlot = displayArrangement?.displays.find(
    (display) => display.index === windowLayout.displayIndex
  );
  if (displaySlot == null) return undefined;

  // The extension places layout windows in the work area too
  const workArea = displayArrangement?.workAreas?.[windowLayout.displayIndex] ?? displaySlot;
  const { left, top, width, height } = resolveRelativeRect(workArea, windowLayout);
  if (![left, top, width, height].every(isFiniteNumber) || width <= 0 || height <= 0) {
    return undefined;
  }

  return {
    left: Math.round(left),
    top: Math.round(top),
    width: Math.round(width),
    height: Math.round(height),
    isFromWorkspaceLayout: true,
  };
}
